import React from 'react';

/**
 * Attrape les erreurs d'affichage et montre ce qui s'est passé.
 *
 * L'application n'avait aucun garde-fou : une erreur dans n'importe quel
 * composant démontait l'arbre React entier et laissait un écran vide. C'est
 * ce que les agents d'EG ont rapporté trois fois de suite sous le nom de
 * « page blanche », sur trois écrans différents. Un écran vide ne dit rien :
 * ni ce qui a cassé, ni s'il faut recharger, ni quoi nous signaler.
 *
 * Le repli reste sobre et en français, donne le message technique replié pour
 * qui veut le recopier, et laisse deux issues : recharger la page, ou revenir
 * à l'accueil quand c'est l'écran lui-même qui est en cause.
 */
interface Props {
  children: React.ReactNode;
}

interface State {
  erreur: Error | null;
}

class GardeFouRendu extends React.Component<Props, State> {
  state: State = { erreur: null };

  static getDerivedStateFromError(erreur: Error): State {
    return { erreur };
  }

  componentDidCatch(erreur: Error, infos: React.ErrorInfo) {
    // La console reste la seule trace côté navigateur : on y écrit la pile
    // complète, pour que le défaut soit reproductible à partir d'un signalement.
    console.error('Erreur d\'affichage', erreur, infos.componentStack);
  }

  render() {
    if (!this.state.erreur) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-eg-voile px-6">
        <div className="max-w-lg w-full rounded-lg border border-eg-bordure bg-white p-8 shadow-sm">
          <h1 className="text-lg font-semibold text-eg-ombre">
            Cet écran n'a pas pu s'afficher
          </h1>
          <p className="mt-3 text-sm text-eg-gris">
            Le travail déjà enregistré n'est pas perdu : l'erreur est
            survenue à l'affichage, pas à l'enregistrement. Rechargez la page
            pour reprendre.
          </p>
          <p className="mt-3 text-sm text-eg-gris">
            Si cela se reproduit au même endroit, signalez-le en indiquant
            l'écran concerné et le message ci-dessous.
          </p>

          <details className="mt-4 rounded-md bg-eg-voile p-3">
            <summary className="cursor-pointer text-sm text-eg-gris">
              Détail technique
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-eg-estompe">
              {this.state.erreur.message || String(this.state.erreur)}
            </pre>
          </details>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-eg-ombre px-4 py-2 text-sm text-eg-blanc"
            >
              Recharger la page
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/workspace/conversations'; }}
              className="rounded-md border border-eg-bordure px-4 py-2 text-sm text-eg-gris"
            >
              Revenir à mes conversations
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default GardeFouRendu;
